// ─────────────────────────────────────────────
//  Schedule domain — pure call-order / go-home logic (no Firebase)
//  Owns: doctor call-order sorting, CRNA go-home sorting, the sheet's
//  go-home-number → wall-clock mapping, and the departure alert window.
//  Mirrors the breaks.js pattern: pure functions, `now` always injected.
// ─────────────────────────────────────────────

// Single source of truth for the alert window: the CRNA leave-time card starts
// blinking this many minutes before their scheduled departure.
export const GO_HOME_ALERT_MINUTES = 15;

// The paper sheet writes afternoon go-home times as small numbers
// (3 → 15:00, 5 → 17:00, 7 → 19:00, 9 → 21:00). Anything non-numeric or
// outside a mappable range (overnight "24", ranges like "7a-7a") is an
// exception the sheet handles by hand — we pass it through as a label.
function toHour24(value) {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  if (n >= 1 && n <= 11) return n + 12; // sheet's afternoon convention
  if (n >= 12 && n <= 23) return n; // already a 24h hour — round-trippable
  return null; // 0, 24, negatives: overnight exceptions
}

const pad2 = (n) => String(n).padStart(2, "0");

// Maps a go-home number to { hour24, label }; exceptions get a passthrough
// label so the UI can still render what the sheet says without crashing.
export function goHomeHourToClock(value) {
  const hour24 = toHour24(value);
  if (hour24 === null) return { hour24: null, label: String(value) };
  return { hour24, label: `${pad2(hour24)}:00` };
}

// Ascending by callOrder — #1 (on call) first; doctors without a number sort
// last, stable, so an unranked doctor never displaces the call list.
export function sortDoctorsByCallOrder(doctors) {
  return [...(doctors || [])].sort((a, b) => {
    const ao = Number.isFinite(a?.callOrder) ? a.callOrder : Infinity;
    const bo = Number.isFinite(b?.callOrder) ? b.callOrder : Infinity;
    return ao - bo;
  });
}

/**
 * Display label for a doctor's call order. Returns the number as a string, or
 * "L" when the doctor has no call-order number.
 *
 * ⚠️ "L" is LOCUMS, not "last" (client correction, 2026-08-04 — this comment
 * previously said "last on call" and was wrong). A doctor without a call
 * number IS a locum; the board already encodes that rule elsewhere
 * (TimelineChartPage: `isLocum: … || e.callOrder == null`). They also happen
 * to sort to the end of the list — sortDoctorsByCallOrder pushes a non-finite
 * callOrder to Infinity — but that is a consequence, not the meaning. Anything
 * spelling this out for a human should say "Locum", not "#L".
 */
export function callOrderLabel(callOrder) {
  return Number.isFinite(callOrder) ? String(callOrder) : "L";
}

// Internal: resolve a value (shiftCode string OR legacy numeric goHomeHour) to a
// 24-hour end hour. Prefers shiftCodeEndHour24 for known shift codes; falls back
// to toHour24 for legacy numeric values. Returns null when no end hour exists.
// NOTE: shiftCodeEndHour24 is defined later in this file; this helper is only
// called at runtime so forward references are fine in module scope.
function resolveEndHour24(value) {
  if (value == null) return null;
  if (typeof value === "string") {
    // Delegate to shift-code parser (handles all 28 codes + unknown → null).
    return shiftCodeEndHour24(value);
  }
  // Legacy numeric goHomeHour path.
  return toHour24(value);
}

// Soonest-to-leave first so the team sees imminent departures at the top;
// reads shiftCode when present, falls back to goHomeHour for legacy data.
// Unset / non-time codes sort last, stable.
export function sortCrnasByGoHome(crnas) {
  return [...(crnas || [])].sort((a, b) => {
    const av = a?.shiftCode != null ? a.shiftCode : a?.goHomeHour;
    const bv = b?.shiftCode != null ? b.shiftCode : b?.goHomeHour;
    const ah = resolveEndHour24(av);
    const bh = resolveEndHour24(bv);
    return (ah === null ? Infinity : ah) - (bh === null ? Infinity : bh);
  });
}

// Minutes from `now` to the go-home wall-clock time (negative once past).
// Accepts a shiftCode string OR a legacy numeric goHomeHour.
// Returns null for non-mappable values so callers can skip alerting.
export function minutesUntilGoHome(value, now) {
  const hour24 = resolveEndHour24(value);
  if (hour24 === null) return null;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  return hour24 * 60 - nowMinutes;
}

// True inside the alert window: at-or-after (hour − threshold) and not yet
// departed (UI-SPEC: currentTime >= goHomeHour*60 − 30).
// Accepts shiftCode OR legacy goHomeHour.
export function isNearGoHome(value, now, thresholdMinutes = GO_HOME_ALERT_MINUTES) {
  const minutes = minutesUntilGoHome(value, now);
  if (minutes === null) return false;
  return minutes >= 0 && minutes <= thresholdMinutes;
}

// Badge text for the GoHomeAlert pill — always text, never color/motion alone.
// Accepts shiftCode OR legacy goHomeHour. Returns "" when no end hour is resolvable.
export function goHomeAlertLabel(value) {
  const hour24 = resolveEndHour24(value);
  if (hour24 === null) return "";
  return `Leaving ${pad2(hour24)}:00`;
}

// ──────────────────────────────────────
//  Shift-code taxonomy (UAT Gap 2 — plan 14)
// ──────────────────────────────────────

// The 28 user-specified shift codes in exact display order.
// String values only — the TIME dropdown uses these directly.
export const SHIFT_CODES = [
  "12", "1", "2", "3", "3L", "4", "4L", "5",
  "11a-7p", "9a-5p", "5L", "7", "8", "7L", "9", "11",
  "7A", "11A", "ASAP", "@3", "@9", "@11",
  "11p-7a", "3p-11p", "4p-11p", "11a-9p", "9a-7p", "8a-4p",
];

// One { value, label } object per shift code for <select> / dropdown use.
// Label is the raw code string — no transformation.
export function shiftCodeOptions() {
  return SHIFT_CODES.map((c) => ({ value: c, label: c }));
}

// Parse the end token of an "Na-Mp" / "Np-Mp" range code.
// e.g. "7p" → 19, "5p" → 17, "7a" → 7, "11p" → 23, "4p" → 16.
// Returns null if the token is not recognisable.
function parseAmPmToken(token) {
  if (!token) return null;
  const lower = token.toLowerCase();
  const isAm = lower.endsWith("a");
  const isPm = lower.endsWith("p");
  if (!isAm && !isPm) return null;
  const num = parseInt(lower.slice(0, -1), 10);
  if (!Number.isFinite(num)) return null;
  if (isAm) {
    // AM: noon (12a) is unusual — map 1-11 as-is; 12 = 0 (midnight); keep simple.
    return num === 12 ? 0 : num;
  }
  // PM: 12pm = noon (12), 1-11pm add 12.
  return num === 12 ? 12 : num + 12;
}

// Derive the 24-hour end hour for a shift code. Returns null when there is
// no meaningful go-home time (noon shift, AM-start ambiguous, or instruction codes).
// Safe for arbitrary string input — never throws.
export function shiftCodeEndHour24(code) {
  if (code == null || typeof code !== "string" || code === "") return null;

  // L-suffixed late codes: strip "L", parse the base afternoon number.
  if (/^\d+L$/i.test(code) && !code.toUpperCase().endsWith("A")) {
    const base = code.slice(0, -1);
    return toHour24(base);
  }

  // AM-start codes (uppercase A suffix, not L): 7A, 11A → no fixed end.
  if (/^\d+A$/.test(code)) return null;

  // Range codes containing a hyphen: "Na-Mp" or "Np-Mp".
  if (code.includes("-")) {
    const parts = code.split("-");
    if (parts.length === 2) {
      const endHour = parseAmPmToken(parts[1]);
      if (endHour !== null) return endHour;
    }
    // Non-parseable range — fall through to null.
    return null;
  }

  // Relative/instruction codes.
  if (code === "ASAP" || code.startsWith("@")) return null;

  // Plain numeric codes — delegate to existing afternoon-convention converter.
  const h = toHour24(code);
  // "12" maps to 12 via toHour24 (noon), but noon is not a go-home time per spec.
  if (h === 12) return null;
  return h; // may be null for out-of-range numbers
}

// Derive the 24-hour START hour for a shift code.
//
// The mirror of shiftCodeEndHour24, which has existed on its own for a long
// time: the app has always known when a shift ENDS (to work out who is staying
// past a boundary) and never when it BEGAN. "How long has this provider been
// free" needs the other half.
//
// Only a RANGE code can answer it. "7a-3p" starts at 07:00; a bare "3p" states
// a go-home time and says nothing about the start, and "7A" / "ASAP" / "@..."
// are instructions rather than windows. Those all return null, and null must be
// rendered as unknown — never as midnight, and never as zero elapsed.
export function shiftCodeStartHour24(code) {
  if (code == null || typeof code !== "string" || code === "") return null;
  if (!code.includes("-")) return null;
  const parts = code.split("-");
  if (parts.length !== 2) return null;
  return parseAmPmToken(parts[0]);
}

// Sort key for ordering by shift end time: finite end hour for timed codes,
// 9000 for everything else (pushes non-time codes to the end of any sort).
export function shiftCodeSortKey(code) {
  const h = shiftCodeEndHour24(code);
  if (h !== null && Number.isFinite(h)) return h;
  return 9000;
}

// ──────────────────────────────────────
//  Dropdown option builders (UAT Gap 4)
// ──────────────────────────────────────

// ── WHEN A PROVIDER LEAVES ──────────────────
//
// The five leaving times this department actually uses, named by the owner on
// 2026-08-20: *"leaving time: 3 pm, 5 pm, 7 pm, 9 pm, 7 am (24h)"*. They are the
// end of shift codes 3, 5, 7, 9 and 24 — the same vocabulary the sheet prints
// and the roster stores, so a time picked here is expressible everywhere else.
//
// ⚠️ WHY THIS LIST IS WRITTEN OUT AND NOT DERIVED. `shiftCodeEndHour24` answers
// 15/17/19/21 for the first four and **null for "24"** — an all-night watch has
// no end hour in that function's terms, which is correct there and useless here.
// Deriving four of five and special-casing the fifth would hide the one entry
// most likely to be got wrong.
//
// ⚠️ AND WHY IT IS NOT THE FULL 15-MINUTE LIST. The departure field used to
// offer every quarter hour from 06:00 to 23:00 — 69 options to express five
// real answers, which is how "21:00" ends up typed into a field that meant to
// say "9 PM". Every value here is still on the 15-minute grid, so the room
// modal, the assignment grid and the timeline drag can all still show it.
export const DEPART_SHIFT_END_TIMES = ["15:00", "17:00", "19:00", "21:00", "07:00"];

// homeTimeOptions — options for the "Home Time" dropdown on CRNA/AA grid rows.
// Uses the existing go-home-number → wall-clock mapping (goHomeHourToClock).
// Values 1..12; label shows the sheet number and the mapped wall-clock time,
// e.g. "3 — 15:00". Value 12 maps to 12:00 noon per the existing toHour24 rules.
export function homeTimeOptions() {
  const options = [];
  for (let n = 1; n <= 12; n++) {
    const { label } = goHomeHourToClock(n);
    options.push({ value: n, label: `${n} — ${label}` });
  }
  return options;
}

// callOrderOptions — options for the attending "Call" dropdown.
// Values 1..max (default 30). Label for 1 flags primary on call; all others are
// the plain number string. Higher number = further down the call list.
export function callOrderOptions(max = 30) {
  const options = [];
  for (let n = 1; n <= max; n++) {
    options.push({
      value: n,
      label: n === 1 ? "1 — primary on call" : String(n),
    });
  }
  return options;
}
